import type { CSSProperties, FC } from 'react'
import { match, P } from 'ts-pattern'
import type { Decoration, User } from 'src/services/apollo/generated/graphql'
import { DecorationType } from 'src/services/apollo/generated/graphql'
import type { DecorationItem } from 'src/services/orpc/decoration'
import { useUserActiveDecoration } from 'src/services/orpc/decoration'
import { cn } from 'src/utils/cx'
import { userHasMembership } from 'src/utils/user'
import AvatarBadge from './AvatarBadge'
import type { DecorationSize } from './AvatarFrame'
import AvatarFrame from './AvatarFrame'
import UserAvatarImage, { type UserAvatarImageProps } from './UserAvatarImage'

type UserAvatarProps = StyleProps & {
  user?: Pick<
    User,
    | 'id'
    | 'username'
    | 'displayName'
    | 'membership'
    | 'activeDecorations'
    | 'avatarMediaId'
    | 'avatarSmallThumbnailMediaUrl'
  > & {
    activeDecorationIds?: string[]
  }
  displayRedDot?: boolean
  /**
   * Override avatar decoration state
   *
   * All fields are required, since this prop should be used only when we need
   * to render specific decorations (e.g. for preview).
   * We don't want some fallback logic jeopardizing the result in such cases.
   */
  decorations?: {
    // `true` to display the user-chosen badge
    badge: boolean | Decoration
    // `true` to display the user-chosen frame
    frame: boolean | DecorationItem
  }
  decorationSize?: DecorationSize
  displayBadgeRemark?: boolean

  avatarImageQuality?: UserAvatarImageProps['quality']
}

/**
 * User avatar
 *
 * Stacking orders of decorations: (From a smaller z-index to a larger z-index)
 * - Avatar image / Username initials
 * - Avatar frame (Only the higher one will be displayed)
 *   * Membership frame
 *   * User-chosen frame
 * - Avatar badge (Only the higher one will be displayed)
 *   * Membership badge
 *   * User badge
 * - Red dot
 */
const UserAvatar: FC<UserAvatarProps> = ({
  user,
  style,
  className,
  displayRedDot,
  decorations,
  decorationSize,
  avatarImageQuality,
  displayBadgeRemark,
}) => {
  const userActiveFrameDecoration = useUserActiveDecoration(
    user,
    DecorationType.AvatarFrame,
  )

  const decorationFrame = match(decorations)
    .returnType<DecorationItem | undefined>()
    .with({ frame: P.select(P.not(P.boolean)) }, frame => frame)
    .with(
      P.union(P.optional(undefined), { frame: true }),
      () => userActiveFrameDecoration,
    )
    .otherwise(() => undefined)

  if (!user) {
    return (
      <div
        className={className}
        style={{ borderRadius: '100%', margin: 0, ...style }}
      />
    )
  }

  const memberStyleEnabled = userHasMembership(user) && !decorationFrame
  return (
    <div className={cn('relative size-10', className)} style={style}>
      <UserAvatarImage
        user={user}
        className="size-full"
        quality={avatarImageQuality}
      />
      {memberStyleEnabled ? (
        membershipFrame
      ) : (
        <AvatarFrame
          className="top-1/2 -translate-x-1/2 left-1/2 -translate-y-1/2 scale-125"
          decoration={decorationFrame}
          decorationSize={decorationSize}
        />
      )}
      <AvatarBadge
        user={user}
        decorationBadge={decorations?.badge ?? true}
        className="absolute -translate-x-1/2 -translate-y-1/2"
        style={{
          left: 'calc(50% + 50% * sin(45deg))',
          top: 'calc(50% + 50% * sin(45deg))',
          width: '40%',
        }}
        remarkDisplayType={displayBadgeRemark ? 'auto' : false}
      />
      {displayRedDot && dot}
    </div>
  )
}

const dot = (
  <div
    // Replicate previous color from MUI
    className="aspect-square bg-[#d32f2f] rounded-full ring-[1px] ring-white dark:ring-black absolute top-0 left-0 -translate-x-1/2 -translate-y-1/2"
    style={{
      left: 'calc(50% + 50% * sin(45deg))',
      top: 'calc(50% - 50% * sin(45deg))',
      width: 'max(10px, 20%)',
    }}
  />
)

const membershipFrame = (
  <>
    <div
      className="bg-gradient-member rounded-full absolute"
      style={
        {
          width: '125%',
          height: '125%',
          left: '-12.5%',
          top: '-12.5%',
          '--frame-width': 'max(1px, min(8%, 6px))',
          /**
           * 80%: * 125% = 100%
           * calc + 1px: For smoothing the edge of the masked ring
           */
          mask: 'radial-gradient(closest-side, transparent 80%, #000 80%, #000 calc(80% + var(--frame-width)), transparent calc(80% + var(--frame-width) + 1px))',
        } as CSSProperties
      }
    />
  </>
)

export default UserAvatar
